import { t } from "@lingui/core/macro";
import { useCallback, useEffect } from "react";

import { useDialogStore } from "@/dialogs/store";
import { useConfirm } from "@/hooks/use-confirm";

/**
 * The registered guard function called by DialogManager.
 * Returns `true` to allow close, `false` to block.
 */
type RegisteredGuardFn = () => Promise<boolean> | boolean;

// Module-level guard registry. Only one dialog is open at a time, so a single
// guard is sufficient. The DialogManager reads this in its onOpenChange handler.
let activeGuard: RegisteredGuardFn | null = null;

/**
 * Returns the currently registered close guard, if any.
 * Called by DialogManager in its onOpenChange handler.
 */
export function getActiveDialogCloseGuard(): RegisteredGuardFn | null {
  return activeGuard;
}

/**
 * Register a close guard for the currently open dialog.
 *
 * Base UI's `DialogRoot.onOpenChange` fires for user-initiated closes
 * (escape key, outside press) but NOT for programmatic state changes.
 * This means `closeDialog()` from the store bypasses the guard naturally.
 *
 * Replaces the Radix-style event handler props (`onEscapeKeyDown`,
 * `onPointerDownOutside`, `onInteractOutside`) that are not supported
 * by Base UI's DialogPopup component.
 *
 * @param canClose  Returns `true` to allow, `false` to hard-block, `"confirm"` to show a confirm dialog.
 */
export function useDialogCloseGuard(canClose: () => "confirm" | boolean) {
  const confirm = useConfirm();
  const closeDialog = useDialogStore((state) => state.closeDialog);

  const guard = useCallback(async (): Promise<boolean> => {
    const result = canClose();

    if (result === true) return true;

    if (result === false) return false;

    // result === "confirm" — show confirmation dialog
    const confirmed = await confirm(t`Are you sure you want to close this dialog?`, {
      description: t`You have unsaved changes that will be lost.`,
      confirmText: t`Leave`,
      cancelText: t`Stay`,
    });

    if (confirmed) {
      // closeDialog() sets open=false programmatically, which bypasses
      // the guard because Base UI's onOpenChange only fires for user interactions.
      closeDialog();
    }

    return false;
  }, [canClose, confirm, closeDialog]);

  useEffect(() => {
    activeGuard = guard;

    return () => {
      activeGuard = null;
    };
  }, [guard]);
}
